import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button} from 'antd';
import campaignService from '../../../services/campaignService';
import advertiserService from '../../../services/advertiserService';
import {activeCampaignsTableColumns, myCampaignsTableColumns} from '../../../components/molecules/Table/columns';
import TableComponent from '../../../components/molecules/Table/component';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import {createCampaignTableChangeHandler, useIsMobile} from "../../../utils/helpers";
import {mobileRenderers} from '../../../components/molecules/Table/tableMobileRenderers';

interface CampaignListPageProps {
    type?: 'my-campaigns' | 'browse-campaigns' | 'best-campaigns' | 'advertiser-campaigns';
    title?: string;
}

const CampaignListPage: React.FC<CampaignListPageProps> = ({type, title}) => {
    const [campaignList, setCampaignList] = useState<any[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const tableColumns = type === 'my-campaigns' ? myCampaignsTableColumns : activeCampaignsTableColumns;
    const variant = type === 'my-campaigns' ? 'myCampaigns' : 'activeCampaigns';
    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const showCreateButton = role === "advertiser";

    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        search: '',
        sortBy: '',
        sortDirection: 'desc' as 'asc' | 'desc'
    });

    const onTableChange = useMemo(() =>
        createCampaignTableChangeHandler(filters, setFilters), [filters]
    );

    useEffect(() => {
        const fetchCampaigns = async () => {
            setLoading(true);
            try {
                let data;
                if (type === 'browse-campaigns') {
                    data = await campaignService.getActiveCampaigns(filters);
                } else if (type === 'advertiser-campaigns') {
                    data = await advertiserService.getCampaignsByAdvertiserId(id as string, filters);
                } else {
                    data = await campaignService.getAuthAdvertiserCampaigns(filters);
                }
                setCampaignList(data);
            } catch (error) {
                console.error('Error fetching campaigns:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCampaigns();
    }, [type, filters]);

    const handleRowClick = (record: any) => {
        setSelectedCampaign(record);
    };

    const handleCreateCampaignClick = () => {
        setIsCreating(true);
    };

    if (selectedCampaign) navigate(`/campaigns/${selectedCampaign._id}`);

    if (isCreating && role === 'advertiser') navigate('/campaigns/create');

    return (
        <div style={{position: 'relative', paddingTop: isMobile ? (showCreateButton ? '50px' : '40px') : '20px'}}>
            {showCreateButton && (
                <div style={{position: 'absolute', top: 0, right: 0, margin: '10px'}}>
                    <Button type="primary" onClick={handleCreateCampaignClick}>
                        {isMobile ? 'Create' : 'Create campaign'}
                    </Button>
                </div>
            )}
            <TableComponent columns={tableColumns} title={title} data={campaignList || []} onRowClick={handleRowClick}
                            loading={loading} onChange={onTableChange} isMobile={isMobile} mobileVariant={variant}
                            mobileRenderers={mobileRenderers} rowKey={(row) => row._id}/>
        </div>
    );
};

export default CampaignListPage;
